<template>
  <div class="inv-print-view">

    <!-- HEADER -->
    <div class="inv-print-header">
      <div class="inv-print-header__left">
        <div class="inv-print-title">[ MACHINES &amp; TOOLS ]</div>
        <div class="inv-print-meta">
          Printed: {{ printDate }}
          &nbsp;·&nbsp; {{ items.length }} records
          <span v-if="filterLabel && filterLabel !== 'All'"> &nbsp;·&nbsp; Filter: {{ filterLabel }}</span>
        </div>
      </div>
      <div class="inv-print-header__right">
        <div class="inv-print-logo">KISMETÁL</div>
        <div class="inv-print-logo-sub">Machines &amp; Tools Register</div>
      </div>
    </div>

    <!-- SUMMARY -->
    <div class="inv-print-summary">
      <span v-for="opt in statusOptions" :key="opt.value" class="inv-print-summary-item">
        <span class="inv-print-summary-num">{{ countByStatus(opt.value) }}</span>
        {{ opt.text }}
      </span>
      <span class="inv-print-summary-sep">|</span>
      <span v-for="opt in typeOptions" :key="'t' + opt.value" class="inv-print-summary-item">
        <span class="inv-print-summary-num">{{ countByType(opt.value) }}</span>
        {{ opt.text }}
      </span>
    </div>

    <!-- TABLE -->
    <table class="inv-print-table">
      <thead>
        <tr>
          <th class="col-id">#</th>
          <th class="col-name">NAME</th>
          <th class="col-serial">SERIAL NUMBER</th>
          <th class="col-model">MODEL</th>
          <th class="col-type">TYPE</th>
          <th class="col-status">STATUS</th>
          <th class="col-date">PURCHASE DATE</th>
          <th class="col-warranty">WARRANTY</th>
          <th class="col-notes">NOTES</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!items.length">
          <td colspan="9" class="inv-print-empty">No records found</td>
        </tr>
        <tr
          v-for="(item, index) in items"
          :key="item.id"
          :class="{
            'inv-print-tr--deleted': item.deleted_at,
            'inv-print-tr--even': index % 2 === 0
          }"
        >
          <td class="col-id inv-print-td--mono inv-print-td--dim">{{ item.id }}</td>

          <td class="col-name">
            <strong class="inv-print-name">{{ item.name }}</strong>
          </td>

          <td class="col-serial inv-print-td--mono">{{ item.serial_number || '—' }}</td>

          <td class="col-model inv-print-td--mono">{{ item.model || '—' }}</td>

          <td class="col-type">
            <span class="inv-print-badge" :class="'inv-print-badge--type-' + item.type">
              {{ typeName(item.type) }}
            </span>
          </td>

          <td class="col-status">
            <span class="inv-print-status" :class="'inv-print-status--' + item.status">
              {{ statusName(item.status) }}
            </span>
          </td>

          <td class="col-date inv-print-td--mono">{{ formatDate(item.purchase_date) }}</td>

          <td class="col-warranty inv-print-td--mono" :class="warrantyClass(item.warranty_expires_at)">
            {{ formatDate(item.warranty_expires_at) }}
          </td>

          <td class="col-warranty inv-print-td--mono" :class="warrantyClass(item.touch_protection_date)">
            {{ formatDate(item.touch_protection_date) }}
          </td>

          <td class="col-notes inv-print-td--notes">{{ item.notes || '' }}</td>
        </tr>
      </tbody>
    </table>

    <!-- FOOTER -->
    <div class="inv-print-footer">
      <span>Total: {{ items.length }} tools</span>
      <span>
        <span class="inv-print-legend inv-print-legend--expired">■</span> Warranty expired &nbsp;
        <span class="inv-print-legend inv-print-legend--soon">■</span> Expiring within 90 days
      </span>
      <span>{{ printDate }}</span>
    </div>

  </div>
</template>

<script>
import { typeName, statusName, formatDate } from '../utils/tools.utils'

export default {
  name: 'ToolsPrintView',
  props: {
    items:        { type: Array,  required: true },
    typeOptions:  { type: Array,  required: true },
    statusOptions:{ type: Array,  required: true },
    filterLabel:  { type: String, default: '' },
  },
  computed: {
    printDate() {
      return new Date().toLocaleDateString('hu-HU', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
  methods: {
    typeName(v)   { return typeName(v, this.typeOptions) },
    statusName(v) { return statusName(v, this.statusOptions) },
    formatDate,
    warrantyClass(d) {
      if (!d) return ''
      const diff = (new Date(d) - new Date()) / 86400000
      if (diff < 0)  return 'inv-print-td--expired'
      if (diff < 90) return 'inv-print-td--soon'
      return ''
    },
    countByStatus(status) { return this.items.filter(i => i.status === status).length },
    countByType(type)     { return this.items.filter(i => i.type === type).length },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.inv-print-view {
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 11px;
  color: #111827;
  background: #fff;
  padding: 28px 32px;
  min-width: 900px;
}

/* ── HEADER ── */
.inv-print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 2px solid #111827;
}
.inv-print-title {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #111827;
}
.inv-print-meta {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  color: #6b7280;
  margin-top: 5px;
  letter-spacing: 0.05em;
}
.inv-print-header__right { text-align: right; }
.inv-print-logo {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #374151;
}
.inv-print-logo-sub {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 8px;
  color: #9ca3af;
  letter-spacing: 0.1em;
  margin-top: 3px;
}

/* ── SUMMARY ── */
.inv-print-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
  padding: 8px 12px;
  background: #f5f6f8;
  border: 1px solid #e2e4ea;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: #6b7280;
  letter-spacing: 0.05em;
}
.inv-print-summary-item { display: flex; align-items: center; gap: 5px; }
.inv-print-summary-num  { font-weight: 700; color: #111827; font-size: 13px; }
.inv-print-summary-sep  { color: #d1d5db; margin: 0 4px; }

/* ── TABLE ── */
.inv-print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
}
.col-id       { width: 40px; }
.col-name     { width: 18%; }
.col-serial   { width: 13%; }
.col-model    { width: 11%; }
.col-type     { width: 9%;  }
.col-status   { width: 9%;  }
.col-date     { width: 10%; }
.col-warranty { width: 10%; }
.col-notes    { width: auto; }

.inv-print-table thead tr { border-bottom: 2px solid #e2e4ea; }
.inv-print-table th {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 8px; font-weight: 700; letter-spacing: 0.12em;
  color: #6b7280; padding: 7px 8px; text-align: left;
  background: #f5f6f8; white-space: nowrap;
}
.inv-print-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f0f1f4;
  vertical-align: top;
  color: #374151;
  line-height: 1.4;
}
.inv-print-tr--even td    { background: #fafafa; }
.inv-print-tr--deleted td { opacity: 0.35; text-decoration: line-through; }

.inv-print-name        { font-weight: 600; color: #111827; font-size: 11px; }
.inv-print-td--mono    { font-family: 'IBM Plex Mono', monospace; font-size: 9px; }
.inv-print-td--dim     { color: #9ca3af; }
.inv-print-td--notes   { font-size: 9px; color: #6b7280; }
.inv-print-td--expired { color: #ef4444 !important; font-weight: 600; }
.inv-print-td--soon    { color: #f59e0b !important; }
.inv-print-empty {
  text-align: center; padding: 32px;
  color: #9ca3af; font-family: 'IBM Plex Mono', monospace;
  font-size: 10px; letter-spacing: 0.1em;
}

/* ── BADGES ── */
.inv-print-badge {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 8px; font-weight: 700; letter-spacing: 0.06em;
  padding: 2px 6px; border: 1px solid; display: inline-block;
}
.inv-print-badge--type-1 { color: #f59e0b; border-color: #f59e0b; background: rgba(245,158,11,0.06); }
.inv-print-badge--type-2 { color: #3b82f6; border-color: #3b82f6; background: rgba(59,130,246,0.06); }

/* ── STATUS ── */
.inv-print-status {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 700; letter-spacing: 0.05em;
}
.inv-print-status--1 { color: #16a34a; }
.inv-print-status--2 { color: #9ca3af; }
.inv-print-status--3 { color: #f59e0b; }
.inv-print-status--4 { color: #ef4444; }

/* ── FOOTER ── */
.inv-print-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 10px;
  border-top: 1px solid #e2e4ea;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  color: #9ca3af;
  letter-spacing: 0.05em;
}
.inv-print-legend         { font-size: 11px; }
.inv-print-legend--expired { color: #ef4444; }
.inv-print-legend--soon    { color: #f59e0b; }

/* ── PRINT MEDIA ── */
@media print {
  .inv-print-view {padding: 0;min-width: unset;}
  .inv-print-title   { font-size: 14px; }
  .inv-print-summary { padding: 6px 10px; font-size: 9px; }
  .inv-print-table th { font-size: 7px; padding: 5px 6px; }
  .inv-print-table td { padding: 5px 6px; font-size: 9px; }
  thead { display: table-header-group; }
  tr    { page-break-inside: avoid; }
  @page { margin: 1.5cm; size: A4 landscape; }
}
</style>